import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Typo, Loading, Flex, Dropdown, Icon, Button, theme, SimpleButton } from '../../../ui';
import { useFileUploaderActions } from '../../fileUploader/hooks/useFileUploaderActions';
import MediaPicker from '../MediaPicker';
import { useTranslation } from 'react-i18next';
import { ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import { UPLOADER_TYPE } from './constants';

const FileLoadingWrapper = styled.div`
  width: ${({ $smallIcon }) => ($smallIcon ? '30px' : '100%')};
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 4rem;
`;

const FileUploader = ({
  saveAction,
  uploaderType = UPLOADER_TYPE.FILE,
  id,
  type,
  accept,
  variant,
  bgcolor,
  fileType,
  label,
  iconName,
  noMediaLibrary,
  smallIcon,
  isBlobToReturn = false,
  callbackIsloading = false,
}) => {
  const { t } = useTranslation();

  const { imageUpload, fileUpload } = useFileUploaderActions();

  const [file, setFile] = useState(null);
  const [fileAsBlob, setFileAsBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [libraryModal, setLibraryModal] = useState(false);

  // Use a ref for the file input to avoid relying on id
  const fileInputRef = useRef(null);
  const saveActionRef = useRef(saveAction);
  useEffect(() => {
    saveActionRef.current = saveAction;
  }, [saveAction]);

  const handleLibraryModal = useCallback(
    (e) => {
      e && e.stopPropagation();
      setLibraryModal(!libraryModal);
    },
    [libraryModal],
  );

  const chooseFile = useCallback(() => {
    // Clear the input value and trigger the file picker using the ref
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  }, []);

  useEffect(() => {
    if (file) {
      const fileData = isBlobToReturn ? fileAsBlob : { id: file.id, url: file.url, name: file.name };
      saveActionRef.current && saveActionRef.current(fileData);
    }
  }, [file, isBlobToReturn, fileAsBlob]);

  //TODO: figure out why we need this stuff
  // useEffect(() => {
  //   function onCompDestroy() {
  //     saveAction(null);
  //     dispatch({ type: 'clearFile' });
  //   }
  //   onCompDestroy();
  // }, [dispatch]) // eslint-disable-line react-hooks/exhaustive-deps
  const onSelectFile = useCallback(
    async (e) => {
      const selectedFile = e.target.files[0];
      setFile(null);
      setFileAsBlob(selectedFile);
      setLoading(true);
      callbackIsloading && callbackIsloading(true);

      // If we only need the blob, skip the API call
      if (isBlobToReturn) {
        setFile({ blob: selectedFile });
        setLoading(false);
        callbackIsloading && callbackIsloading(false);
        return;
      }

      try {
        switch (uploaderType) {
          case UPLOADER_TYPE.IMAGE:
            const imageResult = await imageUpload({ file: selectedFile, mockedType: type });
            setFile(imageResult);
            break;
          case UPLOADER_TYPE.VIDEO:
            const videoResult = await fileUpload(selectedFile);
            setFile(videoResult[0]?.file);
            break;
          default:
            const fileResult = await fileUpload(selectedFile);
            setFile(fileResult[0]?.file);
            break;
        }
      } finally {
        setLoading(false);
        callbackIsloading && callbackIsloading(false);
      }
    },
    [uploaderType, type, imageUpload, fileUpload, callbackIsloading, isBlobToReturn],
  );

  const LoadingWrapper = ({ $smallIcon }) => (
    <FileLoadingWrapper $smallIcon={$smallIcon}>
      <Loading />
    </FileLoadingWrapper>
  );

  const EditFile = ({ loading }) => (
    <Flex align='flex-end' direction='column'>
      <input accept={accept} type='file' style={{ display: 'none' }} id={id} onChange={onSelectFile} ref={fileInputRef} />

      {!noMediaLibrary ? (
        <Dropdown
          label={label || t('globals.attachFile')}
          iconName={iconName || 'Upload'}
          variant={variant || 'customized'}
          bgcolor={bgcolor || theme.colors.PRIMARY_LIGHT}
        >
          <ListItem disablePadding divider>
            <ListItemButton htmlFor={id} onClick={chooseFile} dense>
              <ListItemIcon>
                <Icon.Devices size='small' />
              </ListItemIcon>
              <Typo.Text>{t('media.uploadFromComp')}</Typo.Text>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLibraryModal} dense>
              <ListItemIcon>
                <Icon.Media size='small' />
              </ListItemIcon>
              <Typo.Text>{t('media.uploadFromMedia')}</Typo.Text>
            </ListItemButton>
          </ListItem>
        </Dropdown>
      ) : smallIcon ? (
        <SimpleButton
          size='x-small'
          aria-label={t('globals.attachFile')}
          icon={iconName || 'AttachFile'}
          onClick={chooseFile}
          variant='ghost'
          isLoading={loading}
        />
      ) : (
        <Button
          $variant={variant || 'customized'}
          $bgcolor={bgcolor || theme.colors.PRIMARY_LIGHT}
          icon={iconName || 'Upload'}
          htmlFor={id}
          onClick={chooseFile}
        >
          {label || t('globals.attachFile')}
        </Button>
      )}
    </Flex>
  );

  return loading && !smallIcon ? (
    <LoadingWrapper smallIcon={smallIcon} />
  ) : (
    <>
      <EditFile loading={loading} />
      <MediaPicker saveAction={saveAction} mediaType={fileType || 'all'} isOpen={libraryModal} closeModal={handleLibraryModal} />
    </>
  );
};

export default FileUploader;
